/**
 * Borrower Conditions Preview
 *
 * Status-grouped preview of borrower-assigned conditions.
 * Used ONLY in PortalPreviewSheet for internal staff preview.
 * Does NOT modify the live BorrowerConditionsSection.
 *
 * Groups conditions into:
 *   - Action Needed (red) — open, no uploads or all failed
 *   - Under Review (amber) — open, has any non-failed upload
 *   - Complete (green) — closed/cleared
 *
 * Shows a progress bar and personalized greeting.
 */

import { useMemo } from 'react';
import {
  Upload,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  Download
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { EncompassCondition } from '@/types/approval-letter';
import type { ConditionTemplateInfo, ConditionsPortalUpload } from '@/types/conditions-portal';

type BorrowerStatus = 'needs_action' | 'under_review' | 'completed';

interface BorrowerConditionsPreviewProps {
  conditions: EncompassCondition[];
  uploads: ConditionsPortalUpload[];
  templates: Record<string, ConditionTemplateInfo>;
  conditionNotes?: Record<string, string>;
  borrowerFirstName?: string;
}

const STATUS_CONFIG: Record<BorrowerStatus, {
  label: string;
  accent: string;
  bg: string;
  text: string;
  icon: typeof Upload;
  iconColor: string;
}> = {
  needs_action: {
    label: 'Action Needed',
    accent: 'border-l-red-400',
    bg: 'bg-red-50',
    text: 'text-red-700',
    icon: AlertCircle,
    iconColor: 'text-red-500'
  },
  under_review: {
    label: 'Under Review',
    accent: 'border-l-amber-400',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    icon: Clock,
    iconColor: 'text-amber-500'
  },
  completed: {
    label: 'Complete',
    accent: 'border-l-emerald-400',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    icon: CheckCircle2,
    iconColor: 'text-emerald-500'
  }
};

function getStatus(
  condition: EncompassCondition,
  conditionUploads: ConditionsPortalUpload[]
): BorrowerStatus {
  if (!condition.statusOpen) {
return 'completed';
}

  // If any upload is NOT failed, consider under review
  const hasNonFailedUpload = conditionUploads.some(u => u.encompassSyncStatus !== 'failed');
  if (hasNonFailedUpload) {
return 'under_review';
}

  return 'needs_action';
}

export const BorrowerConditionsPreview = ({
  conditions,
  uploads,
  templates,
  conditionNotes,
  borrowerFirstName
}: BorrowerConditionsPreviewProps) => {
  const grouped = useMemo(() => {
    const groups: Record<BorrowerStatus, Array<{
      condition: EncompassCondition;
      uploads: ConditionsPortalUpload[];
    }>> = {
      needs_action: [],
      under_review: [],
      completed: []
    };

    for (const c of conditions) {
      const cUploads = uploads.filter(u => u.conditionExternalId === c.externalId);
      const status = getStatus(c, cUploads);
      groups[status].push({ condition: c, uploads: cUploads });
    }

    return groups;
  }, [conditions, uploads]);

  const total = conditions.length;
  const completedCount = grouped.completed.length;
  const progressPct = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  if (total === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
        <p className="text-slate-600 font-medium">No conditions assigned to borrower</p>
        <p className="text-sm text-slate-400 mt-1">
          Use "Assign Conditions" → "Assign to Borrower" to select conditions.
        </p>
      </div>
    );
  }

  const greeting = borrowerFirstName
    ? `Hi ${borrowerFirstName}, here are your remaining items`
    : 'Here are your remaining items';

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{greeting}</h2>
        <p className="text-sm text-slate-500 mt-1">
          {completedCount === total
            ? 'All items are complete!'
            : `${total - completedCount} item${total - completedCount !== 1 ? 's' : ''} remaining`}
        </p>
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600 font-medium">Progress</span>
          <span className="text-slate-500">{completedCount} of {total} complete</span>
        </div>
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Status sections */}
      {(['needs_action', 'under_review', 'completed'] as BorrowerStatus[]).map(status => {
        const items = grouped[status];
        if (items.length === 0) {
return null;
}

        const config = STATUS_CONFIG[status];
        const StatusIcon = config.icon;

        return (
          <div key={status} className="space-y-3">
            {/* Section header */}
            <div className="flex items-center gap-2">
              <StatusIcon className={`h-5 w-5 ${config.iconColor}`} />
              <h3 className="font-semibold text-slate-900">{config.label}</h3>
              <Badge variant="secondary" className={`${config.bg} ${config.text} text-xs`}>
                {items.length}
              </Badge>
            </div>

            {/* Condition cards */}
            <div className="space-y-2 pl-7">
              {items.map(({ condition, uploads: cUploads }) => {
                const condId = condition.externalId || condition.id || condition.internalId;
                const note = condId && conditionNotes ? conditionNotes[condId] : undefined;
                const template = condition.externalId ? templates[condition.externalId] : undefined;
                const description = condition.externalDescription || condition.internalDescription;

                return (
                  <div
                    key={condId}
                    className={`relative p-4 rounded-lg bg-white shadow-sm ring-1 ring-black/[0.04] border-l-4 ${config.accent}
                      ${status === 'completed' ? 'opacity-60' : ''}`}
                  >
                    <p className={`text-sm font-medium ${status === 'completed' ? 'text-slate-500' : 'text-slate-900'}`}>
                      {condition.title || 'Untitled Condition'}
                    </p>
                    {description && description !== condition.title && (
                      <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{description}</p>
                    )}

                    {/* Note from lender */}
                    {note && (
                      <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-800 border border-blue-100">
                        <span className="font-medium">Note:</span> {note}
                      </div>
                    )}

                    {/* Template download (preview only — disabled) */}
                    {template?.hasTemplate && status !== 'completed' && (
                      <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-400">
                        <Download className="h-3 w-3" />
                        Download template
                      </div>
                    )}

                    {/* Upload display */}
                    {cUploads.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {cUploads.map(upload => (
                          <div key={upload.id} className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded px-2 py-1">
                            <FileText className="h-3 w-3" />
                            <span className="truncate flex-1">{upload.originalFilename}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload CTA for needs_action (disabled in preview) */}
                    {status === 'needs_action' && (
                      <div className="mt-3">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-400 text-sm cursor-not-allowed">
                          <Upload className="h-4 w-4" />
                          Upload
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BorrowerConditionsPreview;
